import React from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'ramda'
import strings from 'utils/strings'
import { insertIf } from 'utils/types'

import {
  isCanInput,
  isSimpleQuery,
  isAnalogInput,
  isDigitalInput,
  isNativeInput,
  isTimer,
  isLogicSet,
} from 'utils/types/features'
import { singleConditionStrings } from 'i18n/displayConstants/conditions'

import NavList from '../NavList'
import { Lists } from './FunctionNavList.style'

const FunctionNavList = ({
  addCanInput,
  addAnalogInput,
  addDigitalInput,
  addNativeInput,
  outputs,
  logicNodes,
  isSingleConditionOutput,
  isSixteenBitCan,
  singleCondition,
  children,
}) => {
  const { queryType, subType, isNativeOutput } = singleCondition

  const getSingleConditionInputs = () => {
    const hasData = Boolean(subType)

    const isCan = isCanInput(queryType)
    const isSimple = isSimpleQuery(queryType)
    const isAnalog = isSimple && isAnalogInput(subType)
    const isNative = isSimple && isNativeInput(subType)
    const isDigital = isSimple && isDigitalInput(subType)

    const node = {
      ...singleCondition,
      description: isCan ? strings.can_input : singleConditionStrings(subType),
    }

    return [
      {
        hide: !isCan && hasData,
        label: strings.can_input,
        items: isCan && [node],
        navProp: 'uri',
        actionButtonData: {
          hide: hasData,
          onClick: addCanInput,
          label: strings.add_can_input,
        },
      },
      {
        hide: !isAnalog && hasData,
        label: strings.analog_input_pin,
        items: isAnalog && [node],
        navProp: 'uri',
        actionButtonData: {
          hide: hasData,
          onClick: addAnalogInput,
          label: strings.add_analog_input,
        },
      },
      {
        hide: !isNative && hasData,
        label: strings.native_input,
        items: isNative && [node],
        navProp: 'uri',
        actionButtonData: {
          hide: hasData,
          onClick: addNativeInput,
          label: strings.add_native_input,
        },
      },
      {
        hide: (!isDigital && hasData) || isSixteenBitCan,
        label: strings.digital_input_pin,
        items: isDigital && [node],
        navProp: 'uri',
        actionButtonData: {
          hide: hasData,
          onClick: addDigitalInput,
          label: strings.add_digital_input_pin,
        },
      },
    ]
  }

  const getComplexAndTimerInputs = () => {
    const queryNodes = logicNodes.filter((node) => isLogicSet(node.queryType))
    const timerNodes = logicNodes.filter((node) => isTimer(node.queryType))

    return [
      {
        label: strings.logic_sets,
        items: queryNodes,
        navProp: 'uri',
        actionButtonData: {
          hide: true,
        },
      },
      {
        label: strings.timers,
        items: timerNodes,
        navProp: 'uri',
        actionButtonData: {
          hide: true,
        },
      },
    ]
  }

  const isNativeOutputWithSingleInput =
    isNativeOutput && isSingleConditionOutput
  const isNativeOutputWithoutSingleInput =
    isNativeOutput && !isSingleConditionOutput
  const hasLogicSet =
    !isEmpty(logicNodes) && isLogicSet(logicNodes[0] && logicNodes[0].queryType)

  const isNativeOutputWithComplexInput =
    isNativeOutputWithoutSingleInput && (isEmpty(logicNodes) || hasLogicSet)

  const renderInputs =
    !isNativeOutputWithSingleInput || isNativeOutputWithComplexInput

  const handleInputtingConditions = () => {
    if (isSixteenBitCan || isSingleConditionOutput) {
      return getSingleConditionInputs()
    }

    return getComplexAndTimerInputs()
  }

  const listData = [
    {
      label: strings.outputs,
      items: outputs,
      navProp: 'id',
      actionButtonData: {
        hide: true,
      },
    },
    ...insertIf(renderInputs, ...handleInputtingConditions()),
  ]

  return (
    <>
      {children}
      <Lists>
        {listData.map((item) => {
          const { label, items, navProp, actionButtonData, hide } = item
          return (
            !hide && (
              <NavList
                key={label}
                label={label}
                items={items}
                navProp={navProp}
                actionButton={
                  !actionButtonData.hide && {
                    onClick: actionButtonData.onClick,
                    label: actionButtonData.label,
                  }
                }
              />
            )
          )
        })}
      </Lists>
    </>
  )
}

FunctionNavList.propTypes = {
  addCanInput: PropTypes.func,
  addAnalogInput: PropTypes.func,
  addDigitalInput: PropTypes.func,
  addNativeInput: PropTypes.func,
  logicNodes: PropTypes.array,
  outputs: PropTypes.array,
  isSixteenBitCan: PropTypes.bool,
  isSingleConditionOutput: PropTypes.bool,
  singleCondition: PropTypes.object,
  children: PropTypes.node,
}

export default FunctionNavList
